import "./styles.scss";

import Dialog from "components/ui/dialog";
import { BaIconChevronDown, BaIconChevronUp, BaIconEye, BaIconPencil, BaIconRedo, BaIconShare2, BaIconTrash, BaIconUsers } from "components/ui/icons";
import ShareOptions from "components/ui/share-options";
import { logError } from "components/utils/logger";
import { useApp, useQueryDict } from "hooks/app";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { bdayFetch } from "services/utils";

import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";

const CardSendControlCenterActions = ({ cardSend, queryParams, shareOptionsProps, onSignersClick, onClose, onDelete }) => {
  const navigate = useNavigate();
  const { search, pathname, updateSearch, queryDict } = useQueryDict();
  const [deletePopupOpen, setDeletePopupOpen] = useState(false);
  const { showAlert } = useApp();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const { signers, sentDttm, magicLink, isGroupCard, isSingleSenderCard, draftInd, cardId, cardSendId } = cardSend;

  const inviteSigners = signers?.filter((s) => s.inviteEmail) || [];

  const onDeleteCardConfirm = async () => {
    const { errors } = await bdayFetch("/api/1/card/send/delete", { method: "POST", body: { ...queryDict, cardSendId: cardSend.cardSendId } });
    if (errors?.length > 0) {
      showAlert({ message: errors[0].errorMessage, type: "error" });
    } else {
      showAlert({ message: "Success! Card has been deleted." });
      navigate(`${pathname}${updateSearch({ card_send_id: undefined })}`, { replace: true });
      onDelete(cardSend.cardSendId);
      handleClose();
      onClose();
    }
    setDeletePopupOpen(false);
  };

  const onEditCard = () => {
    handleClose();
    onClose();
    // TODO Old card send flow, when not in use, can default to /card/send/create
    const editUrl = isGroupCard || isSingleSenderCard ? `/card/send/create` : `/card/send/edit`;
    navigate(`${editUrl}${updateSearch({ ...queryParams, card_send_id: cardSendId, card_id: cardId, g: undefined, index: -1, ccid: 0, checkout: 1 })}`);
  };

  const onResendCard = () => {
    handleClose();
    onClose();
    navigate(`/card/send/create${updateSearch({ ...queryParams, card_id: cardId, g: 0, index: -1, ccid: 0, card_send_id: undefined, checkout: undefined })}`);
  };

  const onViewCard = () => {
    handleClose();
    onClose();
    navigate(`/card/send/view/${magicLink}${search}`);
  };

  const onSignersCard = () => {
    onSignersClick();
    handleClose();
  };

  const onDeleteCard = () => {
    setDeletePopupOpen(true);
    handleClose();
  };

  const EditMenuItem = (
    <MenuItem onClick={onEditCard} key="edit-menu-item">
      <ListItemIcon className="edit">
        <BaIconPencil />
      </ListItemIcon>
      <ListItemText>{draftInd === 1 ? "Continue Card" : "Edit Card"}</ListItemText>
    </MenuItem>
  );

  const SignersMenuItem = (
    <MenuItem onClick={onSignersCard} key="signers-menu-item">
      <ListItemIcon className="users">
        <BaIconUsers />
      </ListItemIcon>
      <ListItemText>{inviteSigners.length > 0 ? "Invite more signers" : "Invite signers"}</ListItemText>
    </MenuItem>
  );

  const DeleteMenuItem = (
    <MenuItem onClick={onDeleteCard} className="delete" key="delete-menu-item">
      <ListItemIcon className="trash">
        <BaIconTrash />
      </ListItemIcon>
      <ListItemText>Delete Card</ListItemText>
    </MenuItem>
  );

  const ShareMenuItem = (
    <ShareOptions key="share-menu-item" {...shareOptionsProps} onError={handleClose} onClose={handleClose} onSuccess={handleClose}>
      <MenuItem>
        <ListItemIcon className="share">
          <BaIconShare2 />
        </ListItemIcon>
        {shareOptionsProps.buttonTitle}
      </MenuItem>
    </ShareOptions>
  );

  const ViewMenuItem = (
    <MenuItem onClick={onViewCard} key="view-menu-item">
      <ListItemIcon className="view">
        <BaIconEye />
      </ListItemIcon>
      <ListItemText>View final card</ListItemText>
    </MenuItem>
  );

  const ResendMenuItem = (
    <MenuItem onClick={onResendCard} key="resend-menu-item">
      <ListItemIcon className="redo">
        <BaIconRedo />
      </ListItemIcon>
      <ListItemText>Use card again</ListItemText>
    </MenuItem>
  );

  let menuItems = [];
  if (sentDttm) {
    menuItems = [ShareMenuItem, ViewMenuItem, ResendMenuItem];
  } else {
    menuItems.push(EditMenuItem);
    if (draftInd !== 1) {
      menuItems.push(ShareMenuItem);
    }
    if (isGroupCard && draftInd !== 1) {
      menuItems.push(SignersMenuItem);
    }
    if (onDelete) {
      menuItems.push(DeleteMenuItem);
    }
  }

  return (
    <div className="card-send-control-center-actions-container">
      <Button
        id={`card-options-button-${cardSendId}`}
        className={`card-options-button ${open ? "open" : ""}`}
        aria-controls={open ? `card-options-menu-${cardSendId}` : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        color="transparent"
        onClick={handleClick}
        endIcon={open ? <BaIconChevronUp /> : <BaIconChevronDown />}
      >
        CARD OPTIONS
      </Button>
      <Menu
        id={`card-options-menu-${cardSendId}`}
        className="card-options-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transitionDuration={0}
        disablePortal
        MenuListProps={{
          "aria-labelledby": `card-options-button-${cardSendId}`,
        }}
      >
        {menuItems}
      </Menu>
      <Dialog open={deletePopupOpen} title="Are you sure?" subtitle={`You are about to delete the card`} onClose={() => setDeletePopupOpen(false)}>
        <div className="confirm-dialog">
          <Button color="red" onClick={onDeleteCardConfirm} sx={{ marginBottom: "2rem" }}>
            CONFIRM
          </Button>
        </div>
      </Dialog>
    </div>
  );
};

CardSendControlCenterActions.propTypes = {
  cardSend: PropTypes.object.isRequired,
  shareOptionsProps: PropTypes.object,
  onSignersClick: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  queryParams: PropTypes.object,
  onDelete: PropTypes.func,
  withCardOptionsToggle: PropTypes.bool,
};

CardSendControlCenterActions.defaultProps = {
  cardSend: PropTypes.object.isRequired,
  queryParams: {},
  onSignersClick: PropTypes.func.isRequired,
  onDelete: undefined,
};

export default CardSendControlCenterActions;
